import type { TextDetection } from './types';
import type { WorkerInbound, WorkerOutbound, WorkerProgress } from '../../workers/ner.worker';
import { NER_MODEL_ID, NER_QUANTIZATION } from '../../lib/modelCache';
import { probeWebGpu } from '../../lib/webgpu';
import { isMobileDevice } from '../../lib/device';
import NerWorker from '../../workers/ner.worker?worker';

export type NerStatus =
  | { kind: 'idle' }
  | { kind: 'loading'; progress: WorkerProgress }
  | { kind: 'ready'; backend: 'webgpu' | 'wasm' }
  | { kind: 'error'; message: string };

export type NerLoadOptions = {
  onProgress?: (status: NerStatus) => void;
};

let worker: Worker | null = null;
let readyPromise: Promise<{ backend: 'webgpu' | 'wasm' }> | null = null;
let nextRequestId = 1;
const pending = new Map<number, (detections: TextDetection[]) => void>();

const ensureWorker = (): Worker => {
  worker ??= new NerWorker();
  return worker;
};

/**
 * Load the NER model. Idempotent — returns the same promise on repeat calls.
 * Resolves when the worker reports the pipeline is ready.
 */
export const loadNer = (opts: NerLoadOptions = {}): Promise<{ backend: 'webgpu' | 'wasm' }> => {
  if (readyPromise) return readyPromise;

  readyPromise = new Promise<{ backend: 'webgpu' | 'wasm' }>((resolve, reject) => {
    const w = ensureWorker();
    // WebGPU on iOS Safari is unstable for transformers.js — initialising
    // the GPU adapter can blow the per-tab memory budget. Force WASM on
    // phones; modern iPhones run BERT-base-NER at q8 in a few seconds in
    // WASM, which is acceptable. Desktop continues to use WebGPU when
    // available for the speed win.
    const preferWebGpu = !isMobileDevice() && probeWebGpu().supported;

    const onMessage = (ev: MessageEvent<WorkerOutbound>): void => {
      const msg = ev.data;
      if (msg.kind === 'progress') {
        opts.onProgress?.({ kind: 'loading', progress: msg });
      } else if (msg.kind === 'ready') {
        opts.onProgress?.({ kind: 'ready', backend: msg.backend });
        resolve({ backend: msg.backend });
      } else if (msg.kind === 'error') {
        opts.onProgress?.({ kind: 'error', message: msg.message });
        readyPromise = null;
        reject(new Error(msg.message));
      } else {
        const cb = pending.get(msg.id);
        if (cb) {
          pending.delete(msg.id);
          cb(msg.detections);
        }
      }
    };
    w.addEventListener('message', onMessage);

    const init: WorkerInbound = {
      kind: 'init',
      modelId: NER_MODEL_ID,
      quantization: NER_QUANTIZATION,
      preferWebGpu,
    };
    w.postMessage(init);
  });

  return readyPromise;
};

/**
 * Run NER on a string. The model must already be loaded.
 */
export const detectWithNer = async (text: string): Promise<TextDetection[]> => {
  if (!readyPromise) {
    throw new Error('detectWithNer: call loadNer() first');
  }
  await readyPromise;
  const w = ensureWorker();
  const id = nextRequestId++;
  const result = new Promise<TextDetection[]>((resolve) => {
    pending.set(id, resolve);
  });
  const msg: WorkerInbound = { kind: 'detect', id, text };
  w.postMessage(msg);
  return result;
};

/** Reset the worker — used by tests and by toggling the model on/off. */
export const unloadNer = (): void => {
  if (worker) {
    worker.terminate();
    worker = null;
  }
  readyPromise = null;
  pending.clear();
};
